import React, { useEffect, useState } from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useChartData } from './hooks/useChartData';
import MainView from './components/MainView';
import Dashboard from './components/Dashboard';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import { authFetch } from './api';
import { SettingsProvider } from './contexts/SettingsContext';
import { UIProvider } from './contexts/UIContext';
import '@questlabs/react-sdk/dist/style.css';

function RequireAuth({ children }) {
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkSession = async () => {
      const res = await authFetch('get-session');
      if (res?.data?.user) {
        localStorage.setItem('user_id', res.data.user.id);
        setAuthed(true);
      } else {
        localStorage.removeItem('user_id');
        navigate('/login');
      }
      setLoading(false);
    };
    checkSession();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-indigo-50 to-sky-100 dark:from-[#0a0a12] dark:via-[#0f0f1a] dark:to-[#111124]">
        <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-4xl text-primary-600" />
      </div>
    );
  }

  return authed ? children : null;
}

function ChartWrapper() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { chart, loading, error, updateCell, updateCellPriority, bulkUpdateChart } = useChartData(id);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-indigo-50 to-sky-100 dark:from-[#0a0a12] dark:via-[#0f0f1a] dark:to-[#111124] flex-col gap-4">
        <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-4xl text-primary-600" />
        <p className="text-gray-700 dark:text-gray-300 font-semibold">ワークスペースを読み込み中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-indigo-50 to-sky-100 dark:from-[#0a0a12] dark:via-[#0f0f1a] dark:to-[#111124] flex-col gap-4 p-6 text-center">
        <div className="glass-panel rounded-3xl p-8 max-w-sm flex flex-col items-center gap-4">
          <SafeIcon icon={FiIcons.FiAlertCircle} className="text-5xl text-red-500" />
          <p className="text-gray-900 dark:text-white font-bold text-lg">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-2 px-6 py-2.5 glass-btn-primary"
          >
            ダッシュボードに戻る
          </button>
        </div>
      </div>
    );
  }

  if (!chart && !loading) {
    return <Navigate to="/" />;
  }

  return (
    <MainView 
      chartData={chart} 
      updateCell={updateCell} 
      updateCellPriority={updateCellPriority}
      bulkUpdateChart={bulkUpdateChart}
    />
  );
}

function App() {
  return (
    <SettingsProvider>
      <UIProvider>
        <HashRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/" element={
              <RequireAuth>
                <Dashboard />
              </RequireAuth>
            } />
            <Route path="/chart/:id" element={
              <RequireAuth>
                <ChartWrapper />
              </RequireAuth>
            } />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </HashRouter>
      </UIProvider>
    </SettingsProvider>
  );
}

export default App;